// frontend/src/components/PaymentHistory.jsx
import React from "react";
import { getIdentityHeaders, waitForIdentity } from '../lib/identityGoogle';
import { API_BASE, fmtPi } from "../lib/api";

const api = (path) => (API_BASE ? `${API_BASE}${path}` : path);

export default function PaymentHistory() {
  const [loading, setLoading] = React.useState(true);
  const [history, setHistory] = React.useState([]);
  const [expanded, setExpanded] = React.useState(true);

  React.useEffect(() => {
    loadHistory();
  }, []);

  async function loadHistory() {
    setLoading(true);
    try {
      await waitForIdentity();

      const res = await fetch(
        api("/api/creator/analytics/my-styles/payment-history"),
        {
          credentials: "include",
          headers: getIdentityHeaders(),
        }
      );

      if (!res.ok) throw new Error("Failed to load payment history");

      const data = await res.json();
      setHistory(data.history || []);
    } catch (err) {
      console.error("[PaymentHistory] Error:", err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="payment-history-loading">
        <div className="spinner-sm"></div>
        <span>Loading payout history...</span>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="payment-history-empty">
        <p>No payout history yet. Exchange royalties for credits or request a withdrawal to see your history here.</p>
      </div>
    );
  }

  return (
    <div className="payment-history">
      <div className="payment-history-header" onClick={() => setExpanded(!expanded)}>
        <h3>Payout History ({history.length})</h3>
        <span className="toggle-icon">{expanded ? "▼" : "▶"}</span>
      </div>

      {expanded && (
        <div className="payment-history-timeline">
          {history.map((payment) => (
            <PaymentItem key={payment.id} payment={payment} />
          ))}
        </div>
      )}
    </div>
  );
}

function PaymentItem({ payment }) {
  // Determine transaction type
  const isExchange = payment.type === "royalty_exchange" || payment.creditsReceived > 0;
  const isWithdrawal = !isExchange;

  const getStatusConfig = () => {
    switch (payment.status) {
      case "completed":
        return { icon: "✓", color: "#10B981", bg: "#D1FAE5", label: "Completed" };
      case "approved":
        return { icon: "⏳", color: "#F59E0B", bg: "#FEF3C7", label: "Approved" };
      case "pending":
        return { icon: "⏱", color: "#6B7280", bg: "#F3F4F6", label: "Pending" };
      case "rejected":
        return { icon: "✖", color: "#EF4444", bg: "#FEE2E2", label: "Rejected" };
      default:
        return { icon: "?", color: "#6B7280", bg: "#F3F4F6", label: "Unknown" };
    }
  };

  const config = getStatusConfig();

  return (
    <div className="payment-item">
      <div className="payment-icon" style={{ background: config.bg, color: config.color }}>
        {config.icon}
      </div>
      
      <div className="payment-content">
        {isExchange ? (
          <>
            <div className="payment-type">Exchange for Credits</div>
            <div className="payment-details">
              <span className="payment-pi-amount">{fmtPi(payment.amount)}</span>
              <span className="payment-arrow">→</span>
              <span className="payment-credits-amount">{payment.creditsReceived} credits</span>
            </div>
            {payment.rateUsed && (
              <div className="payment-rate">
                Rate: 1 π ≈ {payment.rateUsed.toFixed(2)} credits
                {payment.piPriceUsed && ` (π = $${payment.piPriceUsed.toFixed(2)})`}
              </div>
            )}
          </>
        ) : (
          <>
            <div className="payment-type">Withdrawal to Wallet</div>
            <div className="payment-amount">{fmtPi(payment.amount)}</div>
          </>
        )}
        
        <div className="payment-date">
          {new Date(payment.requestedAt).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>
        
        {payment.completedAt && payment.completedAt !== payment.requestedAt && (
          <div className="payment-completed">
            Completed: {new Date(payment.completedAt).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric'
            })}
          </div>
        )}
      </div>

      <div className="payment-status" style={{ background: config.bg, color: config.color }}>
        {config.label}
      </div>
    </div>
  );
}

// Styles embedded in the component
const styles = `
.payment-history{
  width:100%;
}

.payment-history-header{
  display:flex;
  justify-content:space-between;
  align-items:center;
  cursor:pointer;
  user-select:none;
  padding-bottom:12px;
  border-bottom:1px solid #E5E7EB;
  margin-bottom:16px;
}

.payment-history-header:hover{
  opacity:0.8;
}

.payment-history-header h3{
  font-size:18px;
  font-weight:800;
  color:#111827;
  margin:0;
}

.toggle-icon{
  color:#6D5EF9;
  font-size:14px;
  font-weight:800;
}

.payment-history-timeline{
  display:flex;
  flex-direction:column;
  gap:12px;
}

.payment-item{
  display:flex;
  align-items:flex-start;
  gap:14px;
  padding:16px;
  background:#F9FAFB;
  border:1px solid #E5E7EB;
  border-radius:10px;
  transition: all 0.2s;
}

.payment-item:hover{
  border-color:#6D5EF9;
  box-shadow: 0 2px 8px rgba(109, 94, 249, 0.1);
}

.payment-icon{
  width:40px;
  height:40px;
  border-radius:50%;
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:18px;
  font-weight:800;
  flex-shrink:0;
  margin-top:2px;
}

.payment-content{
  flex:1;
  display:flex;
  flex-direction:column;
  gap:6px;
}

.payment-type{
  font-size:15px;
  font-weight:800;
  color:#111827;
}

.payment-details{
  display:flex;
  align-items:center;
  gap:8px;
  flex-wrap:wrap;
}

.payment-pi-amount{
  font-size:18px;
  font-weight:800;
  color:#6D5EF9;
}

.payment-arrow{
  font-size:16px;
  color:#9CA3AF;
  font-weight:700;
}

.payment-credits-amount{
  font-size:18px;
  font-weight:800;
  color:#10B981;
}

.payment-amount{
  font-size:18px;
  font-weight:800;
  color:#111827;
}

.payment-rate{
  font-size:12px;
  color:#6B7280;
  font-weight:600;
  background:#fff;
  padding:4px 8px;
  border-radius:6px;
  display:inline-block;
  width:fit-content;
}

.payment-date{
  font-size:13px;
  color:#6B7280;
}

.payment-completed{
  font-size:12px;
  color:#10B981;
  font-weight:600;
}

.payment-status{
  padding:6px 12px;
  border-radius:999px;
  font-size:12px;
  font-weight:800;
  text-transform:uppercase;
  letter-spacing:0.05em;
  flex-shrink:0;
  height:fit-content;
}

.payment-history-loading{
  display:flex;
  align-items:center;
  gap:12px;
  padding:20px;
  color:#6B7280;
  font-size:14px;
}

.spinner-sm{
  width:20px;
  height:20px;
  border:3px solid #F3F4F6;
  border-top-color:#6D5EF9;
  border-radius:50%;
  animation:spin 0.8s linear infinite;
}

@keyframes spin{
  to{ transform:rotate(360deg); }
}

.payment-history-empty{
  text-align:center;
  padding:32px 20px;
  color:#6B7280;
  font-size:14px;
  line-height:1.5;
}

@media (max-width:600px){
  .payment-item{
    flex-direction:column;
    align-items:flex-start;
    gap:12px;
  }
  
  .payment-icon{
    margin-top:0;
  }
  
  .payment-content{
    width:100%;
  }
  
  .payment-status{
    align-self:flex-start;
  }
  
  .payment-details{
    font-size:16px;
  }
  
  .payment-pi-amount,
  .payment-credits-amount{
    font-size:16px;
  }
}
`;

// Inject styles
if (typeof document !== "undefined") {
  const styleElement = document.createElement("style");
  styleElement.textContent = styles;
  document.head.appendChild(styleElement);
}